import { trafficConstants } from "../../design/productConfig";
import type { TrafficState } from "../../types/traffic";

export function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

export function targetSpeedMph(activeDriverCount: number): number {
  const n = Math.max(1, Math.floor(activeDriverCount));
  const pressure = Math.pow(
    (n - 1) / trafficConstants.crowdScale,
    trafficConstants.crowdExponent,
  );

  return (
    trafficConstants.floorMph +
    (trafficConstants.freeFlowMph - trafficConstants.floorMph) / (1 + pressure)
  );
}

export function trafficStateForSpeed(speedMph: number): TrafficState {
  if (speedMph >= 60) return "CLEAR";
  if (speedMph >= 45) return "MOVING";
  if (speedMph >= 25) return "SLOW";
  if (speedMph >= 11) return "JAMMED";
  if (speedMph >= 5) return "GRIDLOCK";
  return "PARKING LOT";
}

export function trafficStateWithHysteresis(
  current: TrafficState,
  speedMph: number,
): TrafficState {
  switch (current) {
    case "CLEAR":
      return speedMph < 58.5 ? "MOVING" : current;
    case "MOVING":
      if (speedMph >= 61.5) return "CLEAR";
      return speedMph < 43.5 ? "SLOW" : current;
    case "SLOW":
      if (speedMph >= 46.5) return "MOVING";
      return speedMph < 23.5 ? "JAMMED" : current;
    case "JAMMED":
      if (speedMph >= 26.5) return "SLOW";
      return speedMph < 9.5 ? "GRIDLOCK" : current;
    case "GRIDLOCK":
      if (speedMph >= 12.5) return "JAMMED";
      return speedMph < 3.5 ? "PARKING LOT" : current;
    case "PARKING LOT":
      return speedMph >= 6.5 ? "GRIDLOCK" : current;
  }
}

export function smoothSpeed(
  currentSpeed: number,
  targetSpeed: number,
  deltaSeconds: number,
): number {
  if (deltaSeconds <= 0) return currentSpeed;
  const alpha =
    1 - Math.exp(-deltaSeconds / trafficConstants.speedSmoothingSeconds);
  const next = currentSpeed + (targetSpeed - currentSpeed) * alpha;

  if (targetSpeed < currentSpeed) return Math.max(targetSpeed, next);
  return Math.min(targetSpeed, next);
}

export function trafficWaveMultiplier(
  serverAdjustedMilliseconds: number,
  targetSpeed: number,
  epochSeed = 17,
): number {
  const congestion = clamp01(
    1 -
      (targetSpeed - trafficConstants.floorMph) /
        (trafficConstants.freeFlowMph - trafficConstants.floorMph),
  );
  const time = serverAdjustedMilliseconds / 1000;
  const primary = Math.sin(time * (0.2 + congestion * 0.28) + epochSeed * 0.71);
  const secondary = Math.sin(time * 0.08 + epochSeed * 1.37);
  const amplitude = congestion * 0.18;

  return Math.max(
    0.5 / Math.max(targetSpeed, 0.5),
    1 + amplitude * (primary * 0.72 + secondary * 0.28),
  );
}

export function addedDelayIncrement(
  deltaSeconds: number,
  targetSpeed: number,
): number {
  const congestion = clamp01(
    1 -
      (targetSpeed - trafficConstants.floorMph) /
        (trafficConstants.freeFlowMph - trafficConstants.floorMph),
  );
  return Math.max(0, deltaSeconds) * (0.002 + 0.018 * congestion);
}

export function enduredDelaySeconds(
  distanceMiles: number,
  elapsedSeconds: number,
): number {
  if (distanceMiles <= 0 || elapsedSeconds <= 0) return 0;
  const freeFlowSeconds = (distanceMiles / trafficConstants.freeFlowMph) * 3600;
  return Math.max(0, elapsedSeconds - freeFlowSeconds);
}

export const trafficStateSeverity: Record<TrafficState, number> = {
  CLEAR: 0,
  MOVING: 1,
  SLOW: 2,
  JAMMED: 3,
  GRIDLOCK: 4,
  "PARKING LOT": 5,
};

export function worseTrafficState(
  a: TrafficState,
  b: TrafficState,
): TrafficState {
  return trafficStateSeverity[a] >= trafficStateSeverity[b] ? a : b;
}
